import { Component } from '@theme/component';
import { isClickedOutside, normalizeString, onAnimationEnd } from '@theme/utilities';

/** @returns {any|undefined} */
function getWeglot() {
  if (typeof window === 'undefined') return undefined;
  return /** @type {any} */ (window)['Weglot'];
}

/**
 * Weglot dashboard “published” languages use `enabled: true` on `options.languages` entries.
 * Unpublished (e.g. NL) stay in the project with `enabled: false` — omit from the drawer list.
 * @param {any} entry
 * @returns {boolean}
 */
function isWeglotLanguagePublished(entry) {
  if (typeof entry === 'string') return Boolean(entry);
  if (!entry || typeof entry !== 'object') return false;
  return entry.enabled !== false;
}

/**
 * @param {any} entry
 * @returns {string}
 */
function weglotLanguageCodeFromEntry(entry) {
  if (typeof entry === 'string') return entry;
  return entry?.language_to || entry?.languageTo || entry?.code || entry?.l || '';
}

/**
 * Normalize Weglot project options into ISO codes (original + published destinations).
 * Prefers `options.languages` so unpublished destinations (`enabled: false`) are excluded.
 * @param {any} weglot
 * @returns {{ codes: string[] } | null}
 */
function extractWeglotLanguageCodes(weglot) {
  const o = weglot?.options;
  const fromRoot =
    o?.language_from || o?.original_language || o?.originalLanguage || weglot?.language_from;
  /** @type {string[]} */
  let tos = [];

  // Prefer `options.languages` — only source that exposes dashboard publish (`enabled`).
  if (o && Array.isArray(o.languages) && o.languages.length) {
    tos = o.languages.filter(isWeglotLanguagePublished).map(weglotLanguageCodeFromEntry).filter(Boolean);
  }

  if (!tos.length && Array.isArray(weglot?.language_to)) {
    tos = weglot.language_to.filter(Boolean);
  }

  if (!tos.length && Array.isArray(o?.destination_languages)) {
    tos = o.destination_languages.filter(Boolean);
  }
  if (!tos.length && Array.isArray(o?.available_languages)) {
    tos = o.available_languages.filter(Boolean);
  }
  if (!tos.length && Array.isArray(o?.language_buttons)) {
    tos = o.language_buttons.map(weglotLanguageCodeFromEntry).filter(Boolean);
  }

  const codes = [...new Set([fromRoot, ...tos].filter(Boolean))];
  if (codes.length) return { codes };

  const cur =
    typeof weglot?.getCurrentLang === 'function' ? String(weglot.getCurrentLang() || '').trim() : '';
  if (cur) return { codes: [cur] };

  return null;
}

/**
 * A custom element that displays a localization form.
 *
 * @typedef {object} FormRefs
 * @property {HTMLDivElement} countryList - The country list element.
 * @property {HTMLInputElement} countryInput - The country input element.
 * @property {HTMLUListElement[]} countryListItems - The country list items element.
 * @property {HTMLFormElement} form - The form element.
 * @property {HTMLDivElement} liveRegion - The live region element.
 * @property {HTMLSelectElement | HTMLInputElement} languageInput - Language `<select>` or hidden input (Figma list).
 * @property {HTMLInputElement} [weglotSelectedLang] - Pending Weglot code (Figma drawer list; not posted).
 * @property {HTMLUListElement} [weglotLanguageList] - Weglot language `<ul>` (Figma drawer).
 * @property {HTMLUListElement} [figmaLanguageList] - Shopify language `<ul>` (Figma drawer).
 * @property {HTMLLIElement[]} [languageListItems] - Shopify language rows (Figma drawer).
 * @property {HTMLSpanElement} noResultsMessage - The no results message element.
 * @property {HTMLUListElement} popularCountries - The popular countries element.
 * @property {HTMLInputElement} search - The search input element.
 * @property {HTMLButtonElement} resetButton - The reset button element.
 *
 * @extends {Component<FormRefs>}
 */
class LocalizationFormComponent extends Component {
  /** @type {ReturnType<typeof setInterval> | 0} */
  #weglotPollTimer = 0;

  /** @type {boolean} */
  #weglotLanguageListenerAttached = false;

  /** @type {HTMLDetailsElement | null} */
  #weglotDetailsHost = null;

  connectedCallback() {
    super.connectedCallback();

    this.refs.search && this.refs.search.addEventListener('keydown', this.#onSearchKeyDown);
    this.refs.countryList && this.refs.countryList.addEventListener('keydown', this.#onContainerKeyDown);
    this.refs.countryList && this.refs.countryList.addEventListener('scroll', this.#onCountryListScroll);

    if (this.dataset.weglotLanguages === 'true') {
      this.#setupWeglotDrawerLanguages();

      const details = this.closest('details');
      if (details instanceof HTMLDetailsElement) {
        this.#weglotDetailsHost = details;
        details.addEventListener('toggle', this.#onLocalizationDetailsToggle);
      }
    }

    // Mutual exclusion for figma drawer accordions: opening one closes the other.
    const figmaAccordions = /** @type {NodeListOf<HTMLDetailsElement>} */ (
      this.querySelectorAll('.localization-figma-accordion')
    );
    if (figmaAccordions.length > 1) {
      figmaAccordions.forEach((details) => {
        details.addEventListener('toggle', () => {
          if (details.open) {
            figmaAccordions.forEach((other) => {
              if (other !== details) other.open = false;
            });
          }
        });
      });
    }

    // Resizing the language input can be expensive for browsers that don't support field-sizing: content.
    // Splitting it into separate tasks at least helps when there are multiple localization forms on the page.
    setTimeout(() => this.resizeLanguageInput(), 0);
  }

  disconnectedCallback() {
    if (this.#weglotPollTimer) {
      clearInterval(this.#weglotPollTimer);
      this.#weglotPollTimer = 0;
    }

    const weglot = getWeglot();
    if (this.dataset.weglotLanguages === 'true') {
      this.#weglotDetailsHost?.removeEventListener('toggle', this.#onLocalizationDetailsToggle);
      this.#weglotDetailsHost = null;

      if (typeof window !== 'undefined') {
        window.removeEventListener('weglot:ready', this.#onWeglotReadyEvent);
      }
      if (this.#weglotLanguageListenerAttached && weglot?.off) {
        weglot.off('languageChanged', this.#onWeglotLanguageChanged);
        this.#weglotLanguageListenerAttached = false;
      }
    }

    super.disconnectedCallback();
    this.refs.search && this.refs.search.removeEventListener('keydown', this.#onSearchKeyDown);
    this.refs.countryList && this.refs.countryList.removeEventListener('keydown', this.#onContainerKeyDown);
    this.refs.countryList && this.refs.countryList.removeEventListener('scroll', this.#onCountryListScroll);
  }

  /**
   * Current `country_code` from the form (used when building the localization POST).
   * @returns {string}
   */
  #readPendingCountryCodeFromForm() {
    const { countryInput, form } = this.refs;
    if (form instanceof HTMLFormElement) {
      try {
        const fd = new FormData(form);
        const fromFd = fd.get('country_code');
        if (typeof fromFd === 'string' && fromFd.trim()) return fromFd.trim();
      } catch {
        /* ignore */
      }
    }
    if (countryInput instanceof HTMLInputElement) {
      const v = countryInput.value.trim();
      if (v) return v;
    }
    if (form?.elements?.namedItem) {
      const named = form.elements.namedItem('country_code');
      const el = named instanceof RadioNodeList ? named[0] : named;
      if (el instanceof HTMLInputElement) return el.value.trim();
    }
    return '';
  }

  /**
   * Belt-and-suspenders: ensure `country_code` matches the shopper-selected ISO right before native `form.submit()`.
   * @param {string} snapshot
   */
  #restorePendingCountryCodeOnForm(snapshot) {
    if (!snapshot) return;
    const { countryInput, form } = this.refs;
    if (countryInput instanceof HTMLInputElement && countryInput.value !== snapshot) {
      countryInput.value = snapshot;
    }
    if (form?.elements?.namedItem) {
      const named = form.elements.namedItem('country_code');
      const el = named instanceof RadioNodeList ? named[0] : named;
      if (el instanceof HTMLInputElement && el.value !== snapshot) {
        el.value = snapshot;
      }
    }
  }

  /**
   * Figma drawer + Weglot: **Shopify first**, then Weglot on the **next** page load.
   * Calling `Weglot.switchTo` before the localization POST fights the DOM (Weglot rewrites nodes while the form must stay intact).
   * We store the chosen Weglot code in `sessionStorage` under `NoomaWeglotChrome.DEFERRED_LANG_KEY`; `snippets/weglot-head.liquid` runs `switchTo` on `initialized` / `switchersReady` / `window` `load` (see that file).
   * Uses native `form.submit()` (does not re-dispatch `submit`) so Markets `country_code` + `language_code` POST reliably.
   * @param {SubmitEvent} event
   */
  handleWeglotFigmaSubmit(event) {
    if (this.dataset.weglotLanguages !== 'true') return;

    event.preventDefault();

    const pendingCountrySnapshot = this.#readPendingCountryCodeFromForm();
    this.#restorePendingCountryCodeOnForm(pendingCountrySnapshot);

    const weglot = getWeglot();
    const hidden = this.refs.weglotSelectedLang;
    const langRaw =
      (hidden instanceof HTMLInputElement && hidden.value.trim()) ||
      (typeof weglot?.getCurrentLang === 'function' ? String(weglot.getCurrentLang() || '').trim() : '');
    const lang = String(langRaw);

    try {
      const key = /** @type {{ NoomaWeglotChrome?: { DEFERRED_LANG_KEY?: string } }} */ (window).NoomaWeglotChrome
        ?.DEFERRED_LANG_KEY;
      if (key && typeof sessionStorage !== 'undefined') {
        if (lang) sessionStorage.setItem(key, lang);
        else sessionStorage.removeItem(key);
      }
    } catch {
      /* ignore */
    }

    const formEl = this.refs.form;
    if (!(formEl instanceof HTMLFormElement)) {
      return;
    }

    formEl.submit();
  }

  /** @param {unknown} newLang */
  #onWeglotLanguageChanged = (newLang) => {
    const hidden = this.refs.weglotSelectedLang;
    if (hidden instanceof HTMLInputElement && typeof newLang === 'string' && newLang) {
      hidden.value = newLang;
      this.#syncWeglotLangListAriaCurrent(newLang);
    }
    this.#updateFigmaLanguageSummary();
  };

  /**
   * Mobile drawer: Weglot options often appear only after `initialized`. Opening the outer localization `<details>`
   * can happen before that — retry hydrating the language `<ul>` when the panel becomes visible.
   */
  #onLocalizationDetailsToggle = () => {
    if (!(this.#weglotDetailsHost instanceof HTMLDetailsElement) || !this.#weglotDetailsHost.open) return;
    if (this.dataset.weglotLanguages !== 'true') return;

    const weglot = getWeglot();
    const ul = this.refs.weglotLanguageList;
    if (!weglot || !(ul instanceof HTMLUListElement)) return;

    const onlyPlaceholder =
      ul.querySelectorAll('[data-weglot-lang-item]').length === 0 &&
      Boolean(ul.querySelector('[data-weglot-loading="true"]'));

    if (!onlyPlaceholder) return;

    if (this.#tryHydrateWeglotLanguageOptions(weglot) && !this.#weglotLanguageListenerAttached) {
      weglot.on?.('languageChanged', this.#onWeglotLanguageChanged);
      this.#weglotLanguageListenerAttached = true;
      if (this.#weglotPollTimer) {
        clearInterval(this.#weglotPollTimer);
        this.#weglotPollTimer = 0;
      }
    }
  };

  /** Dispatched from `snippets/weglot-head.liquid` on `initialized` / `switchersReady` / `window` `load` (`weglot:ready`). */
  #onWeglotReadyEvent = () => {
    if (this.#weglotLanguageListenerAttached) return;
    const weglot = getWeglot();
    if (!weglot) {
      return;
    }
    if (this.#tryHydrateWeglotLanguageOptions(weglot)) {
      weglot.on?.('languageChanged', this.#onWeglotLanguageChanged);
      this.#weglotLanguageListenerAttached = true;
      if (this.#weglotPollTimer) {
        clearInterval(this.#weglotPollTimer);
        this.#weglotPollTimer = 0;
      }
    }
  };

  #setupWeglotDrawerLanguages() {
    if (typeof window !== 'undefined') {
      window.addEventListener('weglot:ready', this.#onWeglotReadyEvent);
    }

    this.#onWeglotReadyEvent();

    const tick = () => {
      if (this.#weglotLanguageListenerAttached) return true;

      const weglot = getWeglot();
      if (weglot && this.#tryHydrateWeglotLanguageOptions(weglot)) {
        weglot.on?.('languageChanged', this.#onWeglotLanguageChanged);
        this.#weglotLanguageListenerAttached = true;
        return true;
      }
      return false;
    };

    if (tick()) return;

    let attempts = 0;
    const maxAttempts = 150;

    this.#weglotPollTimer = window.setInterval(() => {
      attempts++;
      if (tick() || attempts >= maxAttempts) {
        if (this.#weglotPollTimer) {
          clearInterval(this.#weglotPollTimer);
          this.#weglotPollTimer = 0;
        }
        if (attempts >= maxAttempts && !this.#weglotLanguageListenerAttached) {
          this.#weglotHydrationFailed();
        }
      }
    }, 100);
  }

  /**
   * @param {any} weglot
   * @returns {boolean} true when the list was filled
   */
  #tryHydrateWeglotLanguageOptions(weglot) {
    const ul = this.refs.weglotLanguageList;
    const hidden = this.refs.weglotSelectedLang;
    if (!(ul instanceof HTMLUListElement) || !(hidden instanceof HTMLInputElement)) {
      return false;
    }

    const pack = extractWeglotLanguageCodes(weglot);
    if (!pack?.codes?.length) {
      return false;
    }

    const current =
      typeof weglot.getCurrentLang === 'function' ? (weglot.getCurrentLang() || pack.codes[0]) : pack.codes[0];

    ul.innerHTML = '';
    hidden.value = current;
    for (const code of pack.codes) {
      ul.appendChild(this.#createWeglotLangListItem(weglot, code, code === current));
    }

    this.#updateFigmaLanguageSummary();
    return true;
  }

  /**
   * @param {any} weglot
   * @param {string} code
   * @param {boolean} isCurrent
   */
  #createWeglotLangListItem(weglot, code, isCurrent) {
    const li = document.createElement('li');
    li.className = 'localization-form__list-item';
    li.dataset.value = code;
    li.dataset.weglotLangItem = 'true';
    li.setAttribute('role', 'option');
    li.setAttribute('tabindex', '-1');
    const label = weglot.getLanguageName?.(code) ?? code.toUpperCase();
    li.setAttribute('aria-label', label);
    li.setAttribute('id', `weglot-lang-${code}`);
    li.setAttribute('on:click', `/selectWeglotLang/${code}`);

    const mark = document.createElement('span');
    mark.className = 'svg-wrapper icon-checkmark';
    const svg = this.querySelector('.localization-form__list .icon-checkmark svg');
    if (svg) mark.appendChild(svg.cloneNode(true));

    const span = document.createElement('span');
    span.className = 'country';
    span.textContent = label;

    li.appendChild(mark);
    li.appendChild(span);
    if (isCurrent) li.setAttribute('aria-current', 'true');
    return li;
  }

  /** @param {string} iso */
  #syncWeglotLangListAriaCurrent(iso) {
    const ul = this.refs.weglotLanguageList;
    if (!(ul instanceof HTMLUListElement)) return;
    for (const node of ul.querySelectorAll('[data-weglot-lang-item]')) {
      if (!(node instanceof HTMLElement)) continue;
      if (node.dataset.value === iso) node.setAttribute('aria-current', 'true');
      else node.removeAttribute('aria-current');
    }
  }

  /**
   * @param {string} code
   * @param {Event} event
   */
  selectWeglotLang = (code, event) => {
    event.preventDefault();
    const hidden = this.refs.weglotSelectedLang;
    if (!(hidden instanceof HTMLInputElement)) return;
    const next = String(code);
    hidden.value = next;
    this.#syncWeglotLangListAriaCurrent(next);
    if (this.dataset.deferSubmit === 'true') {
      this.#updateFigmaLanguageSummary();
    }
  };

  /**
   * Figma drawer: Shopify-published languages as a flat list (same interaction as countries).
   * @param {string} iso
   * @param {Event} event
   */
  selectShopifyDrawerLanguage = (iso, event) => {
    event.preventDefault();
    const { form, languageInput } = this.refs;
    if (!(languageInput instanceof HTMLInputElement)) return;
    const next = String(iso);
    languageInput.value = next;
    const items = this.refs.languageListItems ? [...this.refs.languageListItems] : [];
    for (const item of items) {
      if (item.dataset.value === next) item.setAttribute('aria-current', 'true');
      else item.removeAttribute('aria-current');
    }
    if (this.dataset.deferSubmit === 'true') {
      this.#updateFigmaLanguageSummary();
      return;
    }
    form?.submit();
  };

  #weglotHydrationFailed() {
    const ul = this.refs.weglotLanguageList;
    if (!(ul instanceof HTMLUListElement)) return;

    const msg = this.dataset.weglotError?.trim() || 'Languages could not load.';
    ul.innerHTML = '';
    const li = document.createElement('li');
    li.className = 'localization-form__list-item localization-form__list-item--weglot-loading';
    li.dataset.weglotLoading = 'true';
    li.setAttribute('aria-disabled', 'true');
    const span = document.createElement('span');
    span.className = 'country';
    span.textContent = msg;
    li.appendChild(span);
    ul.appendChild(li);
  }

  /**
   * Handles the keydown event for the container.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onContainerKeyDown = (event) => {
    const { countryInput, countryListItems, form } = this.refs;

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus('UP');
        break;
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus('DOWN');
        break;
      case 'Enter': {
        event.preventDefault();
        event.stopPropagation();
        const items = countryListItems ? [...countryListItems] : [];
        const focusedItem = items.find((item) => item.getAttribute('aria-selected') === 'true');

        if (focusedItem) {
          countryInput.value = focusedItem.dataset.value ?? '';
          if (this.dataset.deferSubmit === 'true') {
            this.#syncCountryAriaCurrent(countryInput.value);
            this.#updateFigmaCountrySummary();
          } else {
            form.submit();
          }
        }
        break;
      }
    }

    if (!this.refs.search) return;

    setTimeout(() => {
      const listItems = this.refs.countryListItems ? [...this.refs.countryListItems] : [];
      const focusableItems = listItems.filter((item) => !item.hasAttribute('hidden'));
      const focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
      const focusedItem = focusableItems[focusedItemIndex];

      if (focusedItem) {
        this.refs.search.setAttribute('aria-activedescendant', focusedItem.id);
      } else {
        this.refs.search.setAttribute('aria-activedescendant', '');
      }
    });
  };

  /**
   * Selects a country.
   *
   * @param {string} countryName - The name of the country to select.
   * @param {Event} event - The event object.
   */
  selectCountry = (countryName, event) => {
    event.preventDefault();
    const { countryInput, form } = this.refs;
    if (!(countryInput instanceof HTMLInputElement)) return;

    countryInput.value = countryName;
    if (this.dataset.deferSubmit === 'true') {
      this.#syncCountryAriaCurrent(countryName);
      this.#updateFigmaCountrySummary();
      return;
    }
    form?.submit();
  };

  /**
   * Changes the language of the localization form.
   *
   * @param {Event} event - The event object.
   */
  changeLanguage(event) {
    const { form, languageInput } = this.refs;
    if (!(languageInput instanceof HTMLSelectElement)) return;

    const value = event.target instanceof HTMLSelectElement ? event.target.value : null;

    if (value) {
      languageInput.value = value;
      this.resizeLanguageInput();
      if (this.dataset.deferSubmit === 'true') {
        this.#updateFigmaLanguageSummary();
        return;
      }
      form.submit();
    }
  }

  /**
   * @param {string} isoCode
   */
  #syncCountryAriaCurrent(isoCode) {
    const { countryListItems } = this.refs;
    const items = countryListItems ? [...countryListItems] : [];
    if (!items.length) return;

    for (const item of items) {
      if (item.dataset.value === isoCode) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }
    }
  }

  /**
   * @param {string | null | undefined} text
   */
  #stripHighlightMarkup(text) {
    if (!text) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = text;
    return tmp.textContent?.trim() ?? '';
  }

  #updateFigmaCountrySummary() {
    const el = this.querySelector('[data-figma-country-summary]');
    const { countryInput, countryListItems } = this.refs;
    const items = countryListItems ? [...countryListItems] : [];
    if (!el || !countryInput || !items.length) return;

    const iso = countryInput.value;
    const li = items.find((item) => item.dataset.value === iso);
    if (!li) return;

    const nameRaw = li.querySelector('.country')?.innerHTML ?? '';
    const name = this.#stripHighlightMarkup(nameRaw);
    const currencyText = li.querySelector('.localization-form__currency')?.textContent?.trim() ?? '';
    // Figma drawer list items render currency as "(EUR)" — strip parens before re-wrapping the summary.
    const currencyCode = currencyText.replace(/[()]/g, '').split(/\s+/)[0] ?? '';
    el.textContent = currencyCode ? `${name} (${currencyCode})` : name;
  }

  #updateFigmaLanguageSummary() {
    const el = this.querySelector('[data-figma-language-summary]');
    if (!el) return;

    const weglotHidden = this.refs.weglotSelectedLang;
    if (weglotHidden instanceof HTMLInputElement && this.dataset.weglotLanguages === 'true') {
      const code = weglotHidden.value;
      const w = getWeglot();
      el.textContent = (w?.getLanguageName?.(code) || code || '').trim();
      return;
    }

    const { languageInput } = this.refs;
    if (!languageInput) return;

    if (languageInput instanceof HTMLSelectElement) {
      const opt = languageInput.selectedOptions[0];
      el.textContent = opt?.textContent?.trim() ?? '';
      return;
    }

    if (languageInput instanceof HTMLInputElement) {
      const items = this.refs.languageListItems ? [...this.refs.languageListItems] : [];
      const li = items.find((item) => item.dataset.value === languageInput.value);
      const raw = li?.querySelector('.country')?.textContent?.trim();
      el.textContent = raw ?? languageInput.value.toUpperCase();
    }
  }

  resizeLanguageInput() {
    const { languageInput } = this.refs;

    if (!(languageInput instanceof HTMLSelectElement) || CSS.supports('field-sizing', 'content')) return;

    // Hide all options except the selected option
    for (const option of languageInput.options) {
      if (!option.selected) {
        option.dataset.optionLabel = option.textContent || '';
        option.innerText = '';
      }
    }

    // Calculate the width of the select element (which is based on the width of the widest option)
    languageInput.style.width = 'fit-content';
    const originalElementWidth = `${Math.ceil(languageInput.offsetWidth) + 1}px`;

    // Fix the width of the select element
    if (languageInput.offsetWidth > 0) {
      languageInput.style.width = originalElementWidth;
    }

    // Add back all option labels
    for (const option of languageInput.options) {
      if (option.dataset.optionLabel) {
        option.textContent = option.dataset.optionLabel;
        delete option.dataset.optionLabel;
      }
    }
  }

  /**
   * Finds matches for a given search value in a country element.
   *
   * @typedef {Object} Options
   * @property {boolean} [matchLabel] - Whether to match the label.
   * @property {boolean} [matchAlias] - Whether to match the alias.
   * @property {boolean} [matchIso] - Whether to match the iso.
   * @property {boolean} [matchCurrency] - Whether to match the currency.
   * @property {boolean} [labelMatchStart] - Whether to match the label start.
   * @property {boolean} [aliasExactMatch] - Whether to match the alias exact match.
   *
   * @typedef {Object} MatchTypes
   * @property {boolean} [label] - Whether the label matches the search value.
   * @property {boolean} [alias] - Whether the alias matches the search value.
   * @property {boolean} [iso] - Whether the iso matches the search value.
   * @property {boolean} [currency] - Whether the currency matches the search value.
   *
   * @param {string} searchValue - The search value to find matches for.
   * @param {HTMLElement} countryEl - The country element to find matches in.
   * @param {Options} options - The options for the search.
   * @returns {MatchTypes} The matches found in the country element.
   */
  #findMatches(
    searchValue,
    countryEl,
    options = {
      // Which data types (label, alias, iso) to match against
      matchLabel: true,
      matchAlias: true,
      matchIso: true,
      matchCurrency: true,
      // If true, the search value must match the start of the label
      labelMatchStart: false,
      // If true, a result will not display unless the search value equals an alias in its entirety
      aliasExactMatch: false,
    }
  ) {
    let matchTypes = {};
    const { aliases, value: iso } = countryEl.dataset;

    if (options.matchLabel) {
      const countryName = normalizeString(countryEl.querySelector('.country')?.textContent ?? '');

      if (!countryName) return matchTypes;

      matchTypes.label = options.labelMatchStart
        ? countryName.startsWith(searchValue)
        : countryName.includes(searchValue);
    }

    if (options.matchCurrency) {
      const currency = normalizeString(countryEl.querySelector('.localization-form__currency')?.textContent ?? '');
      matchTypes.currency = currency.includes(searchValue);
    }

    if (options.matchIso) {
      matchTypes.iso = normalizeString(iso ?? '') == searchValue;
    }

    if (options.matchAlias) {
      const countryAliases = aliases?.split(',').map((alias) => normalizeString(alias));

      if (!countryAliases) return matchTypes;

      matchTypes.alias =
        countryAliases.length > 0 &&
        countryAliases.find((alias) =>
          options.aliasExactMatch ? alias === searchValue : alias.startsWith(searchValue)
        ) !== undefined;
    }

    return matchTypes;
  }

  /**
   * Highlights matching text in a string by wrapping it in <mark> tags.
   *
   * @param {string | null} text - The text to highlight.
   * @param {string} searchValue - The search value to highlight.
   * @returns {string} The text with matching parts wrapped in <mark> tags.
   */
  #highlightMatches(text, searchValue) {
    if (!text || !searchValue) return text ?? '';

    const normalizedText = normalizeString(text);
    const normalizedSearch = normalizeString(searchValue);
    const startIndex = normalizedText.indexOf(normalizedSearch);

    if (startIndex === -1) return text;

    const endIndex = startIndex + normalizedSearch.length;
    const before = text.slice(0, startIndex);
    const match = text.slice(startIndex, endIndex);
    const after = text.slice(endIndex);

    let result = '';
    if (before) {
      result += `<mark>${before}</mark>`;
    }
    result += match;
    if (after) {
      result += `<mark>${after}</mark>`;
    }
    return result;
  }

  /**
   * Filters the countries based on the search value.
   */
  filterCountries() {
    const { countryList, countryListItems, liveRegion, noResultsMessage, popularCountries, resetButton, search } =
      this.refs;
    const { labelResultsCount } = this.dataset;
    const searchValue = normalizeString(search.value);
    let countVisibleCountries = 0;

    resetButton.toggleAttribute('hidden', !searchValue);

    if (popularCountries) {
      popularCountries.toggleAttribute('hidden', Boolean(searchValue));
    }

    const wrapper = this.querySelector('.country-selector-form__wrapper');
    if (wrapper) {
      wrapper.classList.toggle('is-searching', !!searchValue);
    }

    const countryItems = countryListItems ? [...countryListItems] : [];
    for (const countryEl of countryItems) {
      if (searchValue === '') {
        countryEl.removeAttribute('hidden');
        const countrySpan = countryEl.querySelector('.country');
        if (countrySpan) {
          // eslint-disable-next-line no-self-assign
          countrySpan.textContent = countrySpan.textContent;
        }
        countVisibleCountries++;
      } else {
        const matches = this.#findMatches(searchValue, countryEl);

        // In the future, we could reorder/rank filtered results based on the match types
        if (matches.label || matches.alias || matches.iso || matches.currency) {
          countryEl.removeAttribute('hidden');
          const countrySpan = countryEl.querySelector('.country');
          if (countrySpan) {
            countrySpan.innerHTML = this.#highlightMatches(countrySpan.textContent, searchValue);
          }
          countVisibleCountries++;
        } else {
          countryEl.setAttribute('hidden', '');
        }
      }
    }

    if (liveRegion && labelResultsCount) {
      liveRegion.innerText = labelResultsCount.replace('[count]', `${countVisibleCountries}`);
    }

    noResultsMessage.hidden = countVisibleCountries > 0;
    countryList.scrollTop = 0;
  }

  /**
   * Changes the focus of the country list items.
   *
   * @param {string} direction - The direction to change the focus.
   */
  #changeCountryFocus(direction) {
    const { countryListItems } = this.refs;
    const list = countryListItems ? [...countryListItems] : [];
    const focusableItems = list.filter((item) => !item.hasAttribute('hidden'));
    const focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
    const focusedItem = focusableItems[focusedItemIndex];
    let itemToFocus;

    if (direction === 'UP') {
      itemToFocus =
        focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
    } else {
      itemToFocus =
        focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
    }

    if (focusedItem) {
      focusedItem.setAttribute('aria-selected', 'false');
    }
    itemToFocus?.setAttribute('aria-selected', 'true');
    itemToFocus?.focus();
  }

  /**
   * Resets the countries filter.
   *
   * @param {Event} event - The event object.
   */
  resetCountriesFilter(event) {
    const { search } = this.refs;

    event.stopPropagation();
    search.value = '';
    this.filterCountries();
    search.setAttribute('aria-activedescendant', '');
    search.focus();
  }

  /**
   * Handles the keydown event for the search input.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.#onContainerKeyDown(event);
  };

  /**
   * Resets the form.
   */
  resetForm() {
    const { search } = this.refs;

    if (!search) return;

    if (search.value != '') {
      search.value = '';
      this.filterCountries();
      search.setAttribute('aria-activedescendant', '');
    }
  }

  /**
   * Focuses the search input.
   */
  focusSearchInput = () => {
    const { search } = this.refs;

    search?.focus();
  };

  /**
   * Handles the scroll event on the country list.
   *
   * @param {Event} event - The scroll event object.
   */
  #onCountryListScroll = (event) => {
    const countryFilter = this.querySelector('.country-filter');
    const countryList = event.target instanceof HTMLElement ? event.target : null;

    if (countryFilter && countryList) {
      const shouldShowBorder = countryList.scrollTop > 0;
      countryFilter.classList.toggle('is-scrolled', shouldShowBorder);
    }
  };

  /**
   * Expands the country list in the Figma drawer panel to show all countries.
   */
}

/**
 * A custom element that displays a dropdown localization form.
 *
 * @typedef {object} DropdownRefs
 * @property {HTMLButtonElement} button - The button element.
 * @property {HTMLDivElement} panel - The panel element.
 * @property {LocalizationFormComponent} localizationForm - The localization form component.
 *
 * @extends {Component<DropdownRefs>}
 */
class DropdownLocalizationComponent extends Component {
  get isHidden() {
    return this.refs.panel.hasAttribute('hidden');
  }

  /**
   * Toggles the panel.
   */
  toggleSelector() {
    return this.isHidden ? this.showPanel() : this.hidePanel();
  }

  /**
   * Shows the panel.
   */
  showPanel() {
    if (!this.isHidden) return;

    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener('click', this.#handleClickOutside);

    this.refs.panel.removeAttribute('hidden');
    this.refs.button.setAttribute('aria-expanded', 'true');

    onAnimationEnd(this.refs.panel, () => {
      this.#updateWidth();
      this.refs.localizationForm?.focusSearchInput();
    });
  }

  /**
   * Hides the panel.
   */
  hidePanel = () => {
    if (this.isHidden) return;

    this.removeEventListener('keyup', this.#handleKeyUp);
    document.removeEventListener('click', this.#handleClickOutside);

    this.refs.button?.setAttribute('aria-expanded', 'false');
    this.refs.panel.setAttribute('hidden', '');
    this.refs.localizationForm?.resetForm();
  };

  /**
   * Handles the click outside event.
   *
   * @param {PointerEvent} event - The event object.
   */
  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.hidePanel();
    }
  };

  /**
   * Updates the width of the panel.
   */
  #updateWidth() {
    this.style.setProperty('--width', `${this.refs.localizationForm.offsetWidth}px`);
  }

  /**
   * Handles the keyup event.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #handleKeyUp = (event) => {
    switch (event.key) {
      case 'Escape':
        this.hidePanel();
        event.stopPropagation();
        this.refs.button?.focus();
        break;
    }
  };
}

/**
 * A custom element that displays a drawer localization form.
 *
 * @typedef {object} DrawerRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {LocalizationFormComponent} localizationForm - The localization form component.
 *
 * @extends {Component<DrawerRefs>}
 */
class DrawerLocalizationComponent extends Component {
  /**
   * Toggles the dialog.
   *
   * @param {ToggleEvent} event - The event object.
   */
  toggle(event) {
    const { target } = event;
    const { localizationForm } = this.refs;

    if (!localizationForm || !(target instanceof HTMLDetailsElement)) return;

    const countryList = localizationForm.querySelector('.country-selector-form__wrapper');

    if (target.open) {
      if (countryList) countryList.addEventListener('scroll', this.#onCountryListScroll);
      onAnimationEnd(target, localizationForm.focusSearchInput);
    } else {
      countryList?.removeEventListener('scroll', this.#onCountryListScroll);
      localizationForm.resetForm();
    }
  }

  /**
   * Handles the scroll event on the country list.
   *
   * @param {Event} event - The scroll event object.
   */
  #onCountryListScroll = (event) => {
    const countryFilter = this.querySelector('.country-filter');
    const countryList = event.target instanceof HTMLElement ? event.target : null;

    if (countryFilter && countryList) {
      const shouldShowBorder = countryList.scrollTop > 0;
      countryFilter.classList.toggle('is-scrolled', shouldShowBorder);
    }
  };
}

if (!customElements.get('localization-form-component')) {
  customElements.define('localization-form-component', LocalizationFormComponent);
}

if (!customElements.get('dropdown-localization-component')) {
  customElements.define('dropdown-localization-component', DropdownLocalizationComponent);
}

if (!customElements.get('drawer-localization-component')) {
  customElements.define('drawer-localization-component', DrawerLocalizationComponent);
}

// EN/EUR trigger (`data-open-header-localization-drawer`) — header actions + compact footer.
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const trigger = target?.closest?.('[data-open-header-localization-drawer]');
  if (!trigger) return;

  event.preventDefault();
  event.stopPropagation();

  const el = /** @type {any} */ (document.getElementById('header-localization-drawer'));
  if (el && typeof el.showDialog === 'function') el.showDialog();
});
